import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Crown, Heart, Ticket, Trophy, Film, Download, ArrowRight, Calendar,
} from 'lucide-react';
import { DASHBOARD } from '@/constants/testIds';
import { ROUTES } from '@/lib/constants';

function StatCard({ icon: Icon, label, value, hint, testId }) {
  return (
    <Card
      data-testid={testId}
      className="p-4 sm:p-5 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="h-9 w-9 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
          {label}
        </p>
      </div>
      <p className="font-display uppercase text-3xl tracking-wide text-[var(--ns-text)] leading-none">
        {value}
      </p>
      {hint ? <p className="text-xs text-[var(--ns-text-muted)] mt-1.5">{hint}</p> : null}
    </Card>
  );
}

export function OverviewTab({ summary, onGoTab }) {
  const counts = summary?.counts || {};
  const fav = summary?.reference_club;
  const sub = summary?.active_subscription;

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      {/* Bloque destacado: suscripción + club favorito */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        <Card
          data-testid={DASHBOARD.overviewSubscription}
          className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-white to-[var(--ns-amber-50)] border border-[var(--ns-gold)]/30 shadow-[var(--ns-shadow-soft)]"
        >
          <div className="flex items-center gap-2.5 mb-3">
            <div className="h-9 w-9 rounded-xl bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
              <Crown className="h-4 w-4" />
            </div>
            <p className="text-[11px] uppercase tracking-widest text-[var(--ns-gold-700)] font-semibold">
              Suscripción actual
            </p>
          </div>
          {sub ? (
            <>
              <p className="font-display uppercase text-2xl tracking-wide text-[var(--ns-text)]">
                {sub.plan_name || 'Plan activo'}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]">
                  {sub.scope === 'platform' ? 'Plataforma' : sub.scope === 'club' ? 'Club' : (sub.scope || '—')}
                </Badge>
                {sub.cancel_at_period_end ? (
                  <Badge className="bg-amber-100 text-amber-800">Cancelar al final</Badge>
                ) : null}
              </div>
              {sub.ends_at ? (
                <p className="text-xs text-[var(--ns-text-muted)] mt-3">
                  Renovación / fin: {new Date(sub.ends_at).toLocaleDateString('es-ES')}
                </p>
              ) : null}
              <Button
                variant="outline"
                onClick={() => onGoTab?.('subscriptions')}
                className="mt-4 h-9 px-4 rounded-xl bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)]"
              >
                Gestionar suscripción
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-[var(--ns-text)] font-medium">Aún no tienes una suscripción activa.</p>
              <p className="text-xs text-[var(--ns-text-muted)] mt-1">
                Suscríbete a Premium para acceder a contenidos exclusivos.
              </p>
              <Button
                asChild
                className="mt-4 h-9 px-4 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0"
              >
                <Link to="/planes">Ver planes</Link>
              </Button>
            </>
          )}
        </Card>

        <Card
          data-testid={DASHBOARD.overviewFavoriteClub}
          className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
        >
          <div className="flex items-center gap-2.5 mb-3">
            <div className="h-9 w-9 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
              <Heart className="h-4 w-4" />
            </div>
            <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
              Mi club de referencia
            </p>
          </div>
          {fav ? (
            <div className="flex items-center gap-3">
              {fav.logo_url ? (
                <img
                  src={fav.logo_url}
                  alt={fav.name}
                  className="h-14 w-14 rounded-xl object-contain bg-[var(--ns-bg-2)] p-1.5 border border-[var(--ns-border)]"
                />
              ) : (
                <div className="h-14 w-14 rounded-xl bg-[var(--ns-bg-2)] inline-flex items-center justify-center">
                  <Trophy className="h-6 w-6 text-[var(--ns-gold-700)]" />
                </div>
              )}
              <div className="min-w-0">
                <p className="font-display uppercase text-xl tracking-wide text-[var(--ns-text)] truncate">{fav.name}</p>
                {fav.city ? <p className="text-xs text-[var(--ns-text-muted)] mt-0.5">{fav.city}</p> : null}
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-8 px-2 text-[var(--ns-gold-700)] hover:text-[var(--ns-gold)] hover:bg-[var(--ns-amber-50)]"
                >
                  <Link to={`/clubes/${fav.slug}`}>Ir al club <ArrowRight className="h-3 w-3 ml-1" /></Link>
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--ns-text)] font-medium">No has elegido club favorito.</p>
              <p className="text-xs text-[var(--ns-text-muted)] mt-1">Sigue un club para verlo destacado aquí.</p>
              <Button
                variant="outline"
                onClick={() => onGoTab?.('club')}
                className="mt-4 h-9 px-4 rounded-xl bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)]"
              >
                Elegir club favorito
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </>
          )}
        </Card>
      </div>

      {/* Contadores */}
      <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-4">
        <StatCard
          icon={Ticket}
          label="Compras"
          value={counts.purchases ?? 0}
          hint="Eventos, contenidos y descargas"
          testId={DASHBOARD.overviewCountPurchases}
        />
        <StatCard
          icon={Calendar}
          label="Mis partidos"
          value={counts.events ?? 0}
          hint="Accesibles ahora"
          testId={DASHBOARD.overviewCountEvents}
        />
        <StatCard
          icon={Film}
          label="Mis vídeos"
          value={counts.videos ?? 0}
          hint="Contenidos accesibles"
          testId={DASHBOARD.overviewCountVideos}
        />
        <StatCard
          icon={Download}
          label="Mis descargas"
          value={counts.downloads ?? 0}
          hint="Activas"
          testId={DASHBOARD.overviewCountDownloads}
        />
      </div>

      {/* Accesos rápidos */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Button
          data-testid={DASHBOARD.overviewQuickPurchases}
          variant="outline"
          onClick={() => onGoTab?.('purchases')}
          className="h-12 justify-between rounded-xl bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)]"
        >
          <span className="flex items-center gap-2 font-semibold"><Ticket className="h-4 w-4" /> Ver mis compras</span>
          <ArrowRight className="h-4 w-4" />
        </Button>
        <Button
          data-testid={DASHBOARD.overviewQuickContents}
          variant="outline"
          onClick={() => onGoTab?.('videos')}
          className="h-12 justify-between rounded-xl bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)]"
        >
          <span className="flex items-center gap-2 font-semibold"><Film className="h-4 w-4" /> Ver mis contenidos</span>
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {(void ROUTES, null)}
    </div>
  );
}
